import {
  Box,
  BoxProps,
  Button,
  ButtonProps,
  Flex,
  FlexProps,
  IconButton,
  IconButtonProps,
  Modal,
  ModalBody,
  ModalBodyProps,
  ModalContent,
  ModalContentProps,
  ModalOverlay,
  ModalOverlayProps,
  ModalProps,
  Text,
  TextProps,
} from "@chakra-ui/react";
import { AppForm, FormField, FormFieldProps } from "@picovico/ui";
import { merge } from "lodash";
import type { WhatsAppOtpChannel } from "custom-types";
import { ReactNode, useMemo } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { FiArrowLeft, FiShield, FiSmartphone } from "react-icons/fi";
import {
  OTP_SMS_FALLBACK_LABEL,
  OTP_WHATSAPP_DESCRIPTION,
} from "utils/whatsapp";
import * as yup from "yup";

export type MobileVerificationStep = "mobile" | "otp";

export interface MobileVerificationModalStrings {
  logoutLabel?: string;
  mobile?: {
    heading?: string;
    description?: string;
    label?: string;
    placeholder?: string;
    submitLabel?: string;
    submittingLabel?: string;
    invalidMessage?: string;
    requiredMessage?: string;
  };
  otp?: {
    heading?: string;
    description?: string;
    submitLabel?: string;
    submittingLabel?: string;
    resendPrompt?: string;
    resendLabel?: string;
    resendingLabel?: string;
    backLabel?: string;
    invalidMessage?: string;
    /** Before the number when the code went on WhatsApp (whatsapp-messaging 04 §3). */
    whatsappDescription?: string;
    /** The link that asks for the code by SMS instead. */
    smsFallbackLabel?: string;
  };
}

// `otp.description` is left out so it can default to the actual `otpLength`.
const defaultStrings: Required<MobileVerificationModalStrings> = {
  logoutLabel: "Logout",
  mobile: {
    heading: "Add your mobile number",
    description: "We need your mobile number to keep your account secure.",
    label: "Mobile Number",
    placeholder: "Enter your mobile number",
    submitLabel: "Verify",
    submittingLabel: "Sending...",
    invalidMessage: "Mobile number is not valid",
    requiredMessage: "Mobile number is required",
  },
  otp: {
    heading: "Enter verification code",
    submitLabel: "Verify",
    submittingLabel: "Verifying...",
    resendPrompt: "Didn't receive the code?",
    resendLabel: "Resend",
    resendingLabel: "Resending...",
    backLabel: "Back",
    invalidMessage: "Enter a valid code",
    whatsappDescription: OTP_WHATSAPP_DESCRIPTION,
    smsFallbackLabel: OTP_SMS_FALLBACK_LABEL,
  },
};

export interface MobileVerificationModalStyleProps {
  size?: ModalProps["size"];
  overlayProps?: ModalOverlayProps;
  contentProps?: ModalContentProps;
  bodyProps?: ModalBodyProps;
  iconContainerProps?: BoxProps;
  titleProps?: TextProps;
  descriptionProps?: TextProps;
  /** The mobile number highlighted inside the OTP step's description. */
  mobileTextProps?: TextProps;
  formContainerProps?: FlexProps;
  inputProps?: Partial<FormFieldProps>;
  otpInputProps?: Partial<FormFieldProps>;
  primaryButtonProps?: ButtonProps;
  resendContainerProps?: FlexProps;
  resendPromptProps?: TextProps;
  linkButtonProps?: ButtonProps;
  /** "Send by SMS instead", on top of `linkButtonProps`. */
  smsFallbackButtonProps?: ButtonProps;
  backButtonProps?: Partial<IconButtonProps>;
  footerProps?: FlexProps;
  logoutButtonProps?: ButtonProps;
}

export interface MobileVerificationModalProps {
  isOpen: boolean;
  step: MobileVerificationStep;
  mobile: string;
  isSubmittingMobile?: boolean;
  isVerifyingOtp?: boolean;
  isResending?: boolean;
  otpLength?: number;
  mobileIcon?: ReactNode;
  otpIcon?: ReactNode;
  /** The OTP step's icon when the code went on WhatsApp. */
  whatsappIcon?: ReactNode;
  /**
   * Where the last code went, from the answer's `channel` (whatsapp-messaging
   * 02 §10); undefined from a server before WhatsApp, which keeps the old text.
   */
  otpChannel?: WhatsAppOtpChannel | null;
  /** Show "Send by SMS instead" (the gate sets it 20 s after a WhatsApp code that has an SMS fallback). */
  showSmsFallback?: boolean;
  onSendBySms?: () => void | Promise<void>;
  mobileValidationSchema?: yup.AnySchema;
  otpValidationSchema?: yup.AnySchema;
  onSubmitMobile: (mobile: string) => void | Promise<void>;
  onVerifyOtp: (otp: string) => void | Promise<void>;
  onResendOtp?: () => void | Promise<void>;
  onBack?: () => void;
  onLogout: () => void;
  strings?: MobileVerificationModalStrings;
  styleProps?: MobileVerificationModalStyleProps;
}

function MobileVerificationModal({
  isOpen,
  step,
  mobile,
  isSubmittingMobile = false,
  isVerifyingOtp = false,
  isResending = false,
  otpLength = 4,
  mobileIcon,
  otpIcon,
  whatsappIcon,
  otpChannel,
  showSmsFallback = false,
  onSendBySms,
  mobileValidationSchema,
  otpValidationSchema,
  onSubmitMobile,
  onVerifyOtp,
  onResendOtp,
  onBack,
  onLogout,
  strings,
  styleProps = {},
}: MobileVerificationModalProps) {
  const resolvedStrings = useMemo(
    () => merge({}, defaultStrings, strings),
    [strings],
  );

  const mobileSchema = useMemo(
    () =>
      mobileValidationSchema ??
      yup.object().shape({
        mobile: yup
          .string()
          .required(resolvedStrings.mobile.requiredMessage)
          .matches(/^[0-9]{7,15}$/, resolvedStrings.mobile.invalidMessage),
      }),
    [mobileValidationSchema, resolvedStrings],
  );

  const otpSchema = useMemo(
    () =>
      otpValidationSchema ??
      yup.object().shape({
        otp: yup
          .string()
          .required(resolvedStrings.otp.invalidMessage)
          .length(otpLength, resolvedStrings.otp.invalidMessage),
      }),
    [otpValidationSchema, otpLength, resolvedStrings],
  );

  const onWhatsApp = otpChannel === "whatsapp";
  const otpDescription = onWhatsApp
    ? resolvedStrings.otp.whatsappDescription
    : (resolvedStrings.otp.description ??
      `Enter the ${otpLength}-digit code sent to`);

  const isBusy = isSubmittingMobile || isVerifyingOtp || isResending;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => undefined}
      closeOnOverlayClick={false}
      closeOnEsc={false}
      isCentered
      size={styleProps.size ?? "md"}
    >
      <ModalOverlay
        bg="blackAlpha.700"
        backdropFilter="blur(4px)"
        {...styleProps.overlayProps}
      />
      <ModalContent
        mx={4}
        borderRadius="2xl"
        boxShadow="2xl"
        overflow="hidden"
        position="relative"
        {...styleProps.contentProps}
      >
        {step === "otp" && onBack && (
          <IconButton
            aria-label={resolvedStrings.otp.backLabel ?? "Back"}
            icon={<FiArrowLeft />}
            size="sm"
            variant="ghost"
            borderRadius="full"
            position="absolute"
            top={4}
            left={4}
            zIndex={1}
            onClick={onBack}
            isDisabled={isBusy}
            {...styleProps.backButtonProps}
          />
        )}
        <ModalBody
          pt={10}
          pb={4}
          px={{ base: 6, md: 8 }}
          display="flex"
          flexDirection="column"
          alignItems="center"
          {...styleProps.bodyProps}
        >
          <Box
            boxSize={14}
            flexShrink={0}
            borderRadius="full"
            bg="primary.50"
            color="primary.500"
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontSize="26px"
            mb={4}
            {...styleProps.iconContainerProps}
          >
            {step === "mobile"
              ? (mobileIcon ?? <FiSmartphone />)
              : onWhatsApp
                ? (whatsappIcon ?? <FaWhatsapp color="#25D366" />)
                : (otpIcon ?? <FiShield />)}
          </Box>

          <Text
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            {...styleProps.titleProps}
          >
            {step === "mobile"
              ? resolvedStrings.mobile.heading
              : resolvedStrings.otp.heading}
          </Text>

          <Text
            fontSize="sm"
            color="gray.500"
            textAlign="center"
            lineHeight="tall"
            mt={2}
            mb={6}
            maxW="xs"
            {...styleProps.descriptionProps}
          >
            {step === "mobile" ? (
              resolvedStrings.mobile.description
            ) : (
              <>
                {otpDescription}{" "}
                <Text
                  as="span"
                  fontWeight="semibold"
                  color="gray.800"
                  whiteSpace="nowrap"
                  {...styleProps.mobileTextProps}
                >
                  {mobile}
                </Text>
              </>
            )}
          </Text>

          {step === "mobile" && (
            <AppForm<{ mobile: string }>
              apiError={null}
              initialValues={{ mobile: mobile ?? "" }}
              validationSchema={mobileSchema}
              formProps={{ w: "full", ...styleProps.formContainerProps }}
              onSubmit={(values) => onSubmitMobile(values.mobile)}
            >
              <FormField
                name="mobile"
                type="tel"
                label=""
                size="lg"
                placeholder={resolvedStrings.mobile.placeholder}
                required
                {...styleProps.inputProps}
              />
              <Button
                type="submit"
                w="full"
                size="lg"
                mt={4}
                borderRadius="lg"
                isLoading={isSubmittingMobile || isResending}
                isDisabled={isBusy}
                loadingText={resolvedStrings.mobile.submittingLabel}
                {...styleProps.primaryButtonProps}
              >
                {resolvedStrings.mobile.submitLabel}
              </Button>
            </AppForm>
          )}

          {step === "otp" && (
            <AppForm<{ otp: string }>
              apiError={null}
              initialValues={{ otp: "" }}
              validationSchema={otpSchema}
              formProps={{
                w: "full",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                ...styleProps.formContainerProps,
              }}
              onSubmit={(values) => onVerifyOtp(values.otp)}
            >
              <FormField
                name="otp"
                type="otpfield"
                label=""
                fieldLength={otpLength}
                justifyContent="center"
                mt={0}
                mb={0}
                {...styleProps.otpInputProps}
              />
              <Button
                type="submit"
                w="full"
                size="lg"
                mt={6}
                borderRadius="lg"
                isLoading={isVerifyingOtp}
                isDisabled={isBusy}
                loadingText={resolvedStrings.otp.submittingLabel}
                {...styleProps.primaryButtonProps}
              >
                {resolvedStrings.otp.submitLabel}
              </Button>
              {onResendOtp && (
                <Flex
                  align="center"
                  justify="center"
                  gap={1}
                  mt={4}
                  fontSize="sm"
                  {...styleProps.resendContainerProps}
                >
                  <Text color="gray.500" {...styleProps.resendPromptProps}>
                    {resolvedStrings.otp.resendPrompt}
                  </Text>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={onResendOtp}
                    isDisabled={isBusy}
                    isLoading={isResending}
                    loadingText={resolvedStrings.otp.resendingLabel}
                    {...styleProps.linkButtonProps}
                  >
                    {resolvedStrings.otp.resendLabel}
                  </Button>
                </Flex>
              )}
              {showSmsFallback && onSendBySms && (
                <Button
                  variant="link"
                  size="sm"
                  mt={3}
                  onClick={onSendBySms}
                  isDisabled={isBusy}
                  {...styleProps.linkButtonProps}
                  {...styleProps.smsFallbackButtonProps}
                >
                  {resolvedStrings.otp.smsFallbackLabel}
                </Button>
              )}
            </AppForm>
          )}

          <Flex
            w="full"
            justifyContent="center"
            mt={6}
            pt={3}
            borderTop="1px solid"
            borderColor="gray.100"
            {...styleProps.footerProps}
          >
            <Button
              variant="ghost"
              size="sm"
              color="gray.500"
              onClick={onLogout}
              isDisabled={isBusy}
              {...styleProps.logoutButtonProps}
            >
              {resolvedStrings.logoutLabel}
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default MobileVerificationModal;
